import { StatusBadge } from '../ui/StatusBadge'
import { formatDateFull } from '@/lib/dateFormat'
import type { AssemblyType, BomStatus } from '@/services/boms/types'

export interface BomSheetWoodLine {
  id?: string
  sort_order?: number
  part_name: string
  wood_type_raw?: string | null
  material_name?: string | null
  dims_finished_t?: number | null
  dims_finished_w?: number | null
  dims_finished_l?: number | null
  tenon?: string | null
  qty_per: number
  veneer_spec?: string | null
  notes?: string | null
  m3_finished?: number | null
  m3_piece?: number | null
  m3_total?: number | null
  has_missing_dims?: boolean
}

export interface BomSheetHardwareLine {
  id?: string
  sort_order?: number
  name_raw: string
  spec_raw?: string | null
  material_name?: string | null
  qty_per: number
  uom_hint?: string | null
  color_hint?: string | null
  notes?: string | null
}

export interface BomSheetWoodGroup {
  wood_type: string
  total_m3: number
  part_count: number
  qty_total: number
}

export interface BomSheetWoodSummary {
  by_type: BomSheetWoodGroup[]
  total_m3: number
  total_parts: number
  missing_dims_count: number
  has_missing_dims: boolean
}

export function woodToneClass(type: string): string {
  const n = (type || '').toLowerCase()
  if (n.includes('mdf')) return 'bg-blue-50 text-blue-700 border-blue-200'
  if (n.includes('plywood') || n.includes('ply') || n.includes('ván ép')) {
    return 'bg-purple-50 text-purple-700 border-purple-200'
  }
  if (n.includes('cao su') || n.includes('cs') || n.includes('rubber')) {
    return 'bg-emerald-50 text-emerald-800 border-emerald-200'
  }
  if (n.includes('tràm') || n.includes('tram') || n.includes('acacia')) {
    return 'bg-amber-50 text-amber-800 border-amber-200'
  }
  if (n.includes('sồi') || n.includes('oak')) {
    return 'bg-orange-50 text-orange-800 border-orange-200'
  }
  if (n.includes('thông') || n.includes('pine')) {
    return 'bg-teal-50 text-teal-800 border-teal-200'
  }
  return 'bg-slate-100 text-slate-700 border-slate-200'
}

export function calculateWoodSummary(
  lines: BomSheetWoodLine[],
): BomSheetWoodSummary {
  const groupMap = new Map<string, BomSheetWoodGroup>()
  let totalM3 = 0
  let missingCount = 0

  for (const line of lines) {
    const groupKey =
      line.material_name || line.wood_type_raw?.trim() || 'Chưa phân loại'
    let group = groupMap.get(groupKey)
    if (!group) {
      group = {
        wood_type: groupKey,
        total_m3: 0,
        part_count: 0,
        qty_total: 0,
      }
      groupMap.set(groupKey, group)
    }

    group.part_count += 1
    const qty = Number(line.qty_per) || 0
    group.qty_total += qty

    const t = line.dims_finished_t
    const w = line.dims_finished_w
    const l = line.dims_finished_l

    const hasDims =
      t != null && w != null && l != null && t > 0 && w > 0 && l > 0

    if (!hasDims) {
      missingCount += 1
    } else {
      const pieceM3 = (t * w * l) / 1_000_000_000
      const lineM3 = pieceM3 * qty
      group.total_m3 += lineM3
      totalM3 += lineM3
    }
  }

  return {
    by_type: Array.from(groupMap.values()),
    total_m3: totalM3,
    total_parts: lines.length,
    missing_dims_count: missingCount,
    has_missing_dims: missingCount > 0,
  }
}

export interface BomSheetProps {
  // Product info
  itemCode: string
  productName?: string | null
  productDimensions?: string | null
  variantColor?: string | null
  assemblyType: AssemblyType
  mainWoodType?: string | null
  veneerSpec?: string | null

  // BOM info
  bomCode?: string | null
  versionSeq?: number
  editCount?: number
  status?: BomStatus
  updatedAt?: string | null

  // Signatures / Approvals
  preparedBy?: string | null
  checkedBy?: string | null
  checkedBy2?: string | null
  approvedBy?: string | null
  signedOn?: string | null
  notes?: string | null

  // Lines
  woodLines: BomSheetWoodLine[]
  hardwareLines: BomSheetHardwareLine[]

  // Print/view mode
  isPrintMode?: boolean
  className?: string
}

export function BomSheet({
  itemCode,
  productName,
  productDimensions,
  variantColor,
  assemblyType,
  mainWoodType,
  veneerSpec,
  bomCode,
  versionSeq = 1,
  editCount = 0,
  status = 'active',
  updatedAt,
  preparedBy,
  checkedBy,
  checkedBy2,
  approvedBy,
  signedOn,
  notes,
  woodLines,
  hardwareLines,
  isPrintMode = false,
  className = '',
}: BomSheetProps) {
  const isAssembled = assemblyType === 'assembled'
  const modeLabel = isAssembled ? 'RÁP CHẾT (FA)' : 'Tháo rời (KD)'
  const woodSummary = calculateWoodSummary(woodLines)

  return (
    <div
      className={`space-y-6 ${isPrintMode ? 'p-0 text-black' : 'p-6'} ${className}`}
      data-testid="bom-sheet"
    >
      {/* 1. BẢNG THÔNG TIN (TRÊN CÙNG) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 border-b border-slate-200 pb-5">
          <div>
            <div className="flex items-baseline gap-3">
              <span className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
                Mã hàng:
              </span>
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
                {itemCode}
              </h1>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${
                  isAssembled
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : 'bg-amber-50 text-amber-800 border-amber-200'
                }`}
              >
                {modeLabel}
              </span>
            </div>
            {productName && (
              <p className="mt-1 text-base font-medium text-slate-700">
                {productName}
              </p>
            )}
          </div>

          {/* BOM status & version info */}
          <div className="flex flex-col items-start md:items-end gap-1.5">
            <div className="flex items-center gap-2">
              <StatusBadge
                tone={
                  status === 'active'
                    ? 'success'
                    : status === 'archived'
                      ? 'neutral'
                      : 'warning'
                }
                label={
                  status === 'active'
                    ? 'Đang dùng (ACTIVE)'
                    : status === 'archived'
                      ? 'Lưu trữ (ARCHIVED)'
                      : 'Bản nháp (INACTIVE)'
                }
              />
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
              {bomCode && <span className="font-mono">{bomCode}</span>}
              <span>•</span>
              <span className="font-medium text-slate-600">
                {`v${versionSeq}${editCount > 0 ? ` · Đã sửa lần ${editCount}` : ''}`}
              </span>
              {updatedAt && (
                <>
                  <span>•</span>
                  <span>
                    {formatDateFull(updatedAt)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Thông tin kỹ thuật sản phẩm & Ký duyệt */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-xs">
          <div>
            <span className="block text-slate-400 font-medium">Kích thước (D×R×C)</span>
            <span className="mt-0.5 font-semibold text-slate-800">
              {productDimensions || '—'}
            </span>
          </div>
          <div>
            <span className="block text-slate-400 font-medium">Màu sắc (Variant)</span>
            <span className="mt-0.5 font-semibold text-slate-800">
              {variantColor || 'Mặc định'}
            </span>
          </div>
          <div>
            <span className="block text-slate-400 font-medium">Loại gỗ chính</span>
            <span className="mt-0.5 font-semibold text-slate-800">
              {mainWoodType || 'Theo chi tiết'}
            </span>
          </div>
          <div>
            <span className="block text-slate-400 font-medium">Quy cách Veneer</span>
            <span className="mt-0.5 font-semibold text-slate-800">
              {veneerSpec || '—'}
            </span>
          </div>
          <div>
            <span className="block text-slate-400 font-medium">Người lập / KCS</span>
            <span className="mt-0.5 font-semibold text-slate-800">
              {`${preparedBy || '—'}${checkedBy ? ` / ${checkedBy}` : ''}${checkedBy2 ? ` / ${checkedBy2}` : ''}`}
            </span>
          </div>
          <div>
            <span className="block text-slate-400 font-medium">Duyệt / Ngày ký</span>
            <span className="mt-0.5 font-semibold text-slate-800">
              {`${approvedBy || '—'}${signedOn ? ` (${signedOn})` : ''}`}
            </span>
          </div>
        </div>

        {notes && (
          <div className="mt-3 text-xs bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-slate-600">
            <span className="font-semibold text-slate-700">Ghi chú phiếu:</span> {notes}
          </div>
        )}
      </section>

      {/* 2. BẢNG 1 — ĐỊNH MỨC NGUYÊN VẬT LIỆU GỖ */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between bg-slate-900 px-6 py-3 text-white">
          <div className="flex items-center space-x-2">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
            <h2 className="text-xs font-bold tracking-wider uppercase">
              Bảng 1 — Định mức nguyên vật liệu GỖ ({woodLines.length} chi tiết)
            </h2>
          </div>
          <span className="text-xs text-slate-300">Đơn vị tính: 01 sản phẩm</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-xs text-slate-700">
            <thead>
              <tr className="border-b border-slate-300 bg-slate-100 text-center text-xs font-semibold tracking-wider text-slate-800 uppercase">
                <th className="w-10 border-r border-slate-200 p-2.5" rowSpan={2}>
                  STT
                </th>
                <th className="min-w-[200px] border-r border-slate-200 p-2.5 text-left" rowSpan={2}>
                  Tên Chi Tiết Sản Phẩm
                </th>
                <th className="min-w-[130px] border-r border-slate-200 p-2.5" rowSpan={2}>
                  Loại Gỗ / Phôi
                </th>
                <th className="border-r border-slate-200 bg-blue-50/70 p-2 text-blue-950" colSpan={4}>
                  Kích Thước Tinh (mm)
                </th>
                <th className="min-w-[70px] border-r border-slate-200 p-2.5 text-right" rowSpan={2}>
                  SL (SP)
                </th>
                <th className="min-w-[130px] border-r border-slate-200 p-2.5" rowSpan={2}>
                  Ghi Chú Kỹ Thuật
                </th>
                <th className="min-w-[120px] border-r border-slate-200 p-2.5" rowSpan={2}>
                  Quy Cách Veneer
                </th>
                <th className="min-w-[90px] bg-slate-200 p-2.5 text-right text-slate-900" rowSpan={2}>
                  Khối Lượng M³
                </th>
              </tr>
              <tr className="border-b border-slate-300 bg-slate-50 text-center text-xs font-medium text-slate-600">
                <th className="w-14 border-r border-slate-200 bg-blue-50/30 px-2 py-1.5">Dày</th>
                <th className="w-14 border-r border-slate-200 bg-blue-50/30 px-2 py-1.5">Rộng</th>
                <th className="w-16 border-r border-slate-200 bg-blue-50/30 px-2 py-1.5">Dài</th>
                <th className="min-w-[80px] border-r border-slate-200 bg-blue-50/30 px-2 py-1.5">Mộng</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-normal">
              {woodLines.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-6 text-center text-slate-400">
                    Chưa có chi tiết gỗ nào trong định mức.
                  </td>
                </tr>
              ) : (
                woodLines.map((w, idx) => {
                  const t = w.dims_finished_t
                  const width = w.dims_finished_w
                  const l = w.dims_finished_l
                  const hasDims =
                    t != null && width != null && l != null && t > 0 && width > 0 && l > 0
                  const pieceM3 = hasDims ? (t * width * l) / 1_000_000_000 : null
                  const totalLineM3 = pieceM3 != null ? pieceM3 * (w.qty_per || 0) : null
                  const woodTypeLabel = w.material_name || w.wood_type_raw || 'Chưa phân loại'

                  return (
                    <tr key={w.id || idx} className="hover:bg-slate-50 transition-colors">
                      <td className="border-r border-slate-200 p-2.5 text-center font-mono text-slate-400">
                        {w.sort_order ?? idx + 1}
                      </td>
                      <td className="border-r border-slate-200 p-2.5 font-medium text-slate-900">
                        {w.part_name}
                      </td>
                      <td className="border-r border-slate-200 p-2.5">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium border ${woodToneClass(
                            woodTypeLabel,
                          )}`}
                        >
                          {woodTypeLabel}
                        </span>
                      </td>
                      <td className="border-r border-slate-200 p-2.5 text-center font-mono tabular-nums">
                        {t ?? '—'}
                      </td>
                      <td className="border-r border-slate-200 p-2.5 text-center font-mono tabular-nums">
                        {width ?? '—'}
                      </td>
                      <td className="border-r border-slate-200 p-2.5 text-center font-mono tabular-nums">
                        {l ?? '—'}
                      </td>
                      <td className="border-r border-slate-200 p-2.5 text-center text-slate-500">
                        {w.tenon || '—'}
                      </td>
                      <td className="border-r border-slate-200 p-2.5 text-right font-mono font-semibold text-slate-900 tabular-nums">
                        {w.qty_per}
                      </td>
                      <td className="border-r border-slate-200 p-2.5 text-slate-600">
                        {w.notes || '—'}
                      </td>
                      <td className="border-r border-slate-200 p-2.5 text-slate-600">
                        {w.veneer_spec || '—'}
                      </td>
                      <td className="p-2.5 text-right font-mono tabular-nums font-medium text-slate-900 bg-slate-50/50">
                        {totalLineM3 != null ? totalLineM3.toFixed(4) : (
                          <span className="text-amber-600 font-semibold" title="Thiếu kích thước">
                            Thiếu KT
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Chân bảng gỗ: khối lượng theo từng loại gỗ/phôi rồi cộng tổng */}
        <div className="border-t-2 border-slate-300 bg-slate-50 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs">
            <div className="space-y-1.5">
              <span className="text-xs font-bold tracking-wider text-slate-500 uppercase">
                Tổng hợp thể tích M³ theo loại gỗ/phôi (cho 01 sản phẩm):
              </span>
              <div className="flex flex-wrap gap-3">
                {woodSummary.by_type.map((g) => (
                  <div
                    key={g.wood_type}
                    className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-xs"
                  >
                    <span className="font-semibold text-slate-800">{g.wood_type}:</span>
                    <span className="font-mono font-bold text-emerald-700">
                      {g.total_m3.toFixed(3)} M³
                    </span>
                    <span className="text-slate-400">
                      ({g.part_count} chi tiết, SL: {g.qty_total})
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:items-end gap-1 shrink-0">
              <div className="flex items-baseline gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl">
                <span className="text-xs font-medium tracking-wider uppercase text-slate-300">
                  TỔNG M³ (CBM):
                </span>
                <span className="font-mono text-base font-extrabold text-emerald-400">
                  {woodSummary.total_m3.toFixed(3)} M³
                </span>
                <span className="text-xs text-slate-400">
                  {`(${woodSummary.total_parts} chi tiết)`}
                </span>
              </div>

              {woodSummary.has_missing_dims && (
                <span className="inline-flex items-center text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                  {`⚠ ${woodSummary.missing_dims_count} dòng thiếu kích thước (không tính thành 0)`}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 3. BẢNG 2 — ĐỊNH MỨC NGŨ KIM */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between bg-slate-900 px-6 py-3 text-white">
          <div className="flex items-center space-x-2">
            <span className="h-2.5 w-2.5 rounded-full bg-blue-400" />
            <h2 className="text-xs font-bold tracking-wider uppercase">
              {`Bảng 2 — Định mức NGŨ KIM (${hardwareLines.length} vật tư)`}
            </h2>
          </div>
          <span className="text-xs text-slate-300">Đơn vị tính: 01 sản phẩm</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-xs text-slate-700">
            <thead>
              <tr className="border-b border-slate-300 bg-slate-100 text-center text-xs font-semibold tracking-wider text-slate-800 uppercase">
                <th className="w-10 border-r border-slate-200 p-2.5">STT</th>
                <th className="min-w-[220px] border-r border-slate-200 p-2.5 text-left">
                  Tên Vật Tư / Quy Cách
                </th>
                <th className="min-w-[150px] border-r border-slate-200 p-2.5 text-left">
                  Quy Cách Kỹ Thuật
                </th>
                <th className="w-20 border-r border-slate-200 p-2.5 text-right">SL (SP)</th>
                <th className="w-16 border-r border-slate-200 p-2.5 text-center">Đơn Vị</th>
                <th className="w-20 border-r border-slate-200 p-2.5 text-center">Màu Sắc</th>
                <th className="w-20 border-r border-slate-200 p-2.5 text-center">Phân Loại</th>
                <th className="min-w-[150px] p-2.5 text-left">Vị Trí Lắp Đặt / Ghi Chú</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-normal">
              {hardwareLines.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-slate-400">
                    Chưa có ngũ kim nào trong định mức.
                  </td>
                </tr>
              ) : (
                hardwareLines.map((h, idx) => (
                  <tr key={h.id || idx} className="hover:bg-slate-50 transition-colors">
                    <td className="border-r border-slate-200 p-2.5 text-center font-mono text-slate-400">
                      {h.sort_order ?? idx + 1}
                    </td>
                    <td className="border-r border-slate-200 p-2.5 font-medium text-slate-900">
                      {h.name_raw}
                    </td>
                    <td className="border-r border-slate-200 p-2.5 text-slate-600">
                      {h.spec_raw || '—'}
                    </td>
                    <td className="border-r border-slate-200 p-2.5 text-right font-mono font-semibold text-slate-900 tabular-nums">
                      {h.qty_per}
                    </td>
                    <td className="border-r border-slate-200 p-2.5 text-center text-slate-600">
                      {h.uom_hint || 'Cái'}
                    </td>
                    <td className="border-r border-slate-200 p-2.5 text-center text-slate-600">
                      {h.color_hint || '—'}
                    </td>
                    <td className="border-r border-slate-200 p-2.5 text-center font-semibold text-slate-800">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${
                          isAssembled
                            ? 'bg-blue-50 text-blue-800 border border-blue-200'
                            : 'bg-amber-50 text-amber-800 border border-amber-200'
                        }`}
                      >
                        {isAssembled ? 'SX' : 'KD'}
                      </span>
                    </td>
                    <td className="p-2.5 text-slate-600">{h.notes || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 4. CHÂN TRANG IN ẤN (Print footer) */}
      <footer className="hidden print:flex items-center justify-between pt-4 border-t border-slate-300 text-xs text-slate-500 mt-6">
        <div>
          Định mức kỹ thuật: <strong>{itemCode}</strong>
          {productName ? ` — ${productName}` : ''} ({modeLabel})
        </div>
        <div className="font-mono">
          Phiên bản: <strong>v{versionSeq}{editCount && editCount > 0 ? ` · Đã sửa lần ${editCount}` : ''}</strong> [{status?.toUpperCase()}]
        </div>
        <div>
          Hệ thống ERP Mộc Phát · Ngày in: {formatDateFull(new Date())}
        </div>
      </footer>
    </div>
  )
}
